import axios, { type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { getAuthToken } from './apiClient';

// Environment-based Analytics API configuration
const ANALYTICS_API_BASE_URL = (import.meta.env.VITE_ANALYTICS_API_URL || import.meta.env.VITE_API_BASE_URL.replace('/core/api/v1', '/api/analytics')).replace(/\/+$/, '');

// Create analytics API axios instance
const analyticsApiClient = axios.create({
  baseURL: ANALYTICS_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout for analytics queries
});

// Request interceptor to add JWT token and impersonation token
analyticsApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No JWT token available for analytics request:', config.url);
    }

    // Check for impersonation session and add token
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        if (impersonationData.sessionToken) {
          config.headers['X-Impersonation-Token'] = impersonationData.sessionToken;
          console.log('👤 Analytics API: Adding impersonation token to request');
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Add debug=true to URL
    if (config.url) {
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}debug=true`;
    }

    console.log(`📊 Analytics API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ Analytics request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
analyticsApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ Analytics API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    const { response, config } = error;

    if (response) {
      console.error(`❌ Analytics API Error: ${response.status} ${config?.method?.toUpperCase()} ${config?.url}`);
      console.error('📦 Error response:', response.data);
    } else if (error.request) {
      console.error('❌ Analytics Network Error: No response received');
    } else {
      console.error('❌ Analytics Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export { analyticsApiClient, ANALYTICS_API_BASE_URL };
export default analyticsApiClient;
